import React, { useEffect, useState, useRef } from 'react';
import TextTransition, { presets } from 'react-text-transition';
import { motion } from 'framer-motion';
import Navbar from "./navbar/Navbar";
import {LucideFacebook, LucideInstagram} from "lucide-react";

const SLOGANS = [
    "Crafting Cinematic Stories",
    "Frame by Frame",
    "Capture. Create. Inspire."
];

const textVariants = {
    initial: { opacity: 0, y: 20 },
    animate: {
        opacity: 1,
        y: 0,
        transition: { duration: 1 }
    }
};

const scrollButtonVariants = {
    animate: {
        opacity: [0, 1, 0],
        y: [0, 10, 0],
        transition: {
            duration: 2,
            repeat: Infinity,
        },
    },
};

// WhatsApp button animation
const whatsAppButtonVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: {
        scale: 1,
        opacity: 1,
        transition: {
            type: "spring",
            stiffness: 260,
            damping: 20,
            delay: 1.5
        }
    },
    hover: {
        scale: 1.1,
        transition: { duration: 0.2 }
    }
};

export default function Hero() {
    const [isMobile, setIsMobile] = useState(false);
    const [index, setIndex] = useState(0);
    const gtranslateScriptLoaded = useRef(false); // Track if script has been loaded

    // Replace this with your actual WhatsApp number (international format without +)
    const whatsAppNumber = "+40720909792";
    // Default message that will appear in the chat
    const defaultMessage = "Hello! I'd like to inquire about your video services.";

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const intervalId = setInterval(() => setIndex((prevIndex) => prevIndex + 1), 3000);
        return () => clearInterval(intervalId);
    }, []);

    // Effect to load GTranslate script if not already loaded
    useEffect(() => {
        // Skip if script is already loaded by Navbar or previously
        if (gtranslateScriptLoaded.current || window.gtranslateSettings) {
            return;
        }

        // Define GTranslate settings
        window.gtranslateSettings = {
            "default_language": "en",
            "detect_browser_language": true,
            "languages": ["en", "ro"], // English and Romanian
            "wrapper_selector": ".gtranslate_wrapper",
            "flag_size": 24,
            "switcher_horizontal_position": "inline"
        };

        // Create and append the GTranslate script
        const script = document.createElement('script');
        script.src = "https://cdn.gtranslate.net/widgets/latest/dwf.js";
        script.defer = true;
        document.head.appendChild(script);

        gtranslateScriptLoaded.current = true;
    }, []);

    const scrollToContact = () => {
        const contactSection = document.getElementById("Contact");
        if (contactSection) {
            contactSection.scrollIntoView({ behavior: "smooth" });
        }
    };

    // Function to open WhatsApp with default message
    const openWhatsApp = () => {
        const encodedMessage = encodeURIComponent(defaultMessage);
        window.open(`https://wa.me/${whatsAppNumber}?text=${encodedMessage}`, '_blank');
    };

    return (
        <div className="hero-container relative flex flex-col items-center justify-center h-screen overflow-hidden">
            <Navbar/>

            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20">
                <img src="/logo.png" alt="Logo" className="h-16 md:h-20 mt-4"/>
            </div>

            {/* Desktop View: Translation + Social Icons */}
            <div className="hidden md:flex absolute items-center z-30 ml-auto right-6 mt-6 top-4">
                {/* Translation widget for desktop */}
                <div className="gtranslate_wrapper mr-4"></div>

                {/* Social icons for desktop */}
                <div className="flex md:space-x-6 space-x-4">
                    <a href="https://www.instagram.com/shotbyspectrum/" target="_blank" rel="noreferrer" className="text-white">
                        <LucideInstagram className="w-6 h-6"/>
                    </a>
                    <a href="https://www.facebook.com/profile.php?id=61565984460545" target="_blank" rel="noreferrer" className="text-white">
                        <LucideFacebook className="w-6 h-6"/>
                    </a>
                </div>
            </div>

            {/* Mobile View: Split Components */}
            <div className="md:hidden">
                {/* Translation widget for mobile - positioned higher */}
                <div className="absolute z-30 left-1/2 transform -translate-x-1/2" style={{top: "100px"}}>
                    <div className="gtranslate_wrapper"></div>
                </div>

                {/* Social icons for mobile - same position as desktop */}
                <div className="absolute flex space-x-4 z-30 ml-auto right-6 mt-6 top-4">
                    <a href="https://www.instagram.com/shotbyspectrum/" target="_blank" rel="noreferrer" className="text-white">
                        <LucideInstagram className="w-6 h-6"/>
                    </a>
                    <a href="https://www.facebook.com/profile.php?id=61565984460545" target="_blank" rel="noreferrer" className="text-white">
                        <LucideFacebook className="w-6 h-6"/>
                    </a>
                </div>
            </div>

            {/* Background Video */}
            <video
                className="absolute top-0 left-0 w-full h-full object-cover z-[-1] opacity-80"
                autoPlay
                loop
                muted
                playsInline
                src={isMobile ? "https://spectrum-studios.b-cdn.net/mobile.mp4" : "https://spectrum-studios.b-cdn.net/desktop.mov"}
            ></video>

            {/* Grain Overlay */}
            <div
                className="absolute top-0 left-0 w-full h-full bg-gray-900 opacity-30 mix-blend-overlay pointer-events-none"></div>

            {/* Center Text with Animation */}
            <div className="text-white text-center z-10 px-4 mb-8">
                <TextTransition
                    springConfig={presets.wobbly}
                    className="text-4xl md:text-6xl font-extrabold tracking-wide"
                >
                    {SLOGANS[index % SLOGANS.length]}
                </TextTransition>
            </div>

            {/* Contact Button */}
            <motion.div
                className="z-10 mt-4"
                variants={textVariants}
                initial="initial"
                animate="animate"
            >
                <button
                    className="px-8 py-3 mt-6 border border-white rounded-lg bg-transparent text-white font-light hover:bg-white hover:text-black transition"
                    onClick={scrollToContact}
                >
                    Contact Us
                </button>
            </motion.div>

            {/* Animated Scroll Icon */}
            <motion.img
                className="w-16 z-10 absolute bottom-36"
                src="/scroll.png"
                alt="Scroll Down"
                variants={scrollButtonVariants}
                animate="animate"
            />

            {/* WhatsApp Button */}
            <motion.div
                className="fixed bottom-6 right-6 z-50"
                variants={whatsAppButtonVariants}
                initial="initial"
                animate="animate"
                whileHover="hover"
                onClick={openWhatsApp}
            >
                <div className="bg-green-500 p-3 rounded-full shadow-lg cursor-pointer flex items-center justify-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="white">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                </div>
                {/* Tooltip */}
                <div className="absolute bottom-full right-0 mb-2 bg-white text-gray-800 px-3 py-1 rounded shadow-md text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    Chat with us
                </div>
            </motion.div>
        </div>
    );
}
